import { useEffect, useRef, useState } from 'react'

import Bugsnag from '@bugsnag/js'
import { useRouteError } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

import { isThirdPartyError, shouldIgnoreBugsnagError } from '~/portal/utils/bugsnag'
import { ComponentError } from '~/shared/components/ErrorBoundary/ComponentError'
import { errorStack } from '~/shared/components/ErrorBoundary/helpers.ts'
import { NetworkErrorNotification } from '~/shared/components/ErrorBoundary/NetworkErrorNotification'
import PageNotFound from '~/shared/components/PageNotFound'
import { useNotification } from '~/shared/hooks/useNotification.ts'
import { Environment } from '~/shared/types/environment'
import { isExpectedHttpStatus, isNetworkError } from '~/shared/utils/http'

export const ErrorBoundaryComponent = () => {
  const error: Error | any = useRouteError()
  const [errorId, setErrorId] = useState<string>()
  const reportedRef = useRef(false)

  const { flashError } = useNotification()

  useEffect(() => {
    if (reportedRef.current) { return }
    reportedRef.current = true

    const uniqueErrorId = uuidv4().split('-')[0]

    if (isNetworkError(error?.status)) {
      setErrorId(uniqueErrorId)
      flashError({
        message: <NetworkErrorNotification
          status={error.status}
          statusText={error.statusText}
          errorId={uniqueErrorId}
        />,
        dismiss: true,
        duration: 'Infinity',
      })
    }

    if (globalThis.window['ENV']?.bugsnagApiKey && globalThis.window['ENV']?.env !== Environment.LOCAL && error) {
      const errorMessage = errorStack(error)
      // Keep the original error so its stack reaches Bugsnag; fall back for non-Error route errors
      const reportedError = error instanceof Error ? error : new Error(errorMessage)
      Bugsnag?.notify(
        reportedError,
        (event) => {
          if (shouldIgnoreBugsnagError(errorMessage) || isThirdPartyError(event)) {
            return false
          }

          if (isNetworkError(error?.status)) {
            // 502/503/504 are infrastructure blips, not app bugs
            event.severity = 'warning'
          } else {
            setErrorId(uniqueErrorId)
          }
          event.addMetadata('error', {
            id: uniqueErrorId,
          })
          event.groupingHash = errorMessage
        })
    }
  }, [error])

  if (isExpectedHttpStatus(error?.status, 404)) {
    return <PageNotFound />
  }

  return <ComponentError errorId={errorId} />
}